import { Alert, Button, Group, Stack, Text, Textarea } from "@mantine/core";
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CancelTaskButton } from "./CancelTaskButton";
import type { TaskResponse } from "../features/queue/api";
import {
  isPlanStateConflict,
  planRefusalText,
} from "../features/queue/planRefusal";
import { useApprovePlan } from "../features/queue/useApprovePlan";
import { useRejectPlan } from "../features/queue/useRejectPlan";

// Баннер «Ожидает одобрения плана» для status=awaiting_plan_approval.
// Кнопка «Утвердить план» — прямой POST на /approve_plan. Кнопка
// «Отклонить план» — раскрывает inline-форму с Textarea «Причина
// (опционально)» и кнопкой подтверждения → POST на /reject_plan.
export function PlanApprovalBanner({
  task,
}: {
  readonly task: TaskResponse;
}): ReactElement {
  const { t } = useTranslation();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const approveMutation = useApprovePlan(task.id);
  const rejectMutation = useRejectPlan(task.id);

  const handleApprove = (): void => {
    approveMutation.mutate(undefined);
  };

  const handleRejectConfirm = (): void => {
    const trimmed = reason.trim();
    const variables = trimmed.length > 0 ? { reason: trimmed } : {};
    rejectMutation.mutate(variables, {
      onSuccess: () => {
        setRejectOpen(false);
        setReason("");
      },
    });
  };

  return (
    <Alert color="blue" title={t("task.planApprovalBannerTitle")}>
      <Stack gap="sm">
        <Text>{t("task.planApprovalBannerBody")}</Text>
        {approveMutation.isError &&
        !isPlanStateConflict(approveMutation.error) ? (
          <Alert color="red" title={t("task.approvePlanErrorTitle")}>
            {planRefusalText(approveMutation.error, t)}
          </Alert>
        ) : null}
        {rejectMutation.isError &&
        !isPlanStateConflict(rejectMutation.error) ? (
          <Alert color="red" title={t("task.rejectPlanErrorTitle")}>
            {planRefusalText(rejectMutation.error, t)}
          </Alert>
        ) : null}
        <Group>
          <Button
            color="green"
            onClick={handleApprove}
            loading={approveMutation.isPending}
            disabled={rejectMutation.isPending}
          >
            {approveMutation.isPending
              ? t("task.approvePlanInFlight")
              : t("task.approvePlanButton")}
          </Button>
          <CancelTaskButton task={task} />
          {!rejectOpen ? (
            <Button
              color="orange"
              variant="light"
              onClick={() => {
                setRejectOpen(true);
              }}
              disabled={approveMutation.isPending}
            >
              {t("task.rejectPlanButton")}
            </Button>
          ) : null}
        </Group>
        {rejectOpen ? (
          <Stack gap="xs">
            <Textarea
              label={t("task.rejectPlanReasonLabel")}
              placeholder={t("task.rejectPlanReasonPlaceholder")}
              value={reason}
              onChange={(e) => {
                setReason(e.currentTarget.value);
              }}
              rows={3}
            />
            <Group>
              <Button
                color="orange"
                onClick={handleRejectConfirm}
                loading={rejectMutation.isPending}
              >
                {rejectMutation.isPending
                  ? t("task.rejectPlanInFlight")
                  : t("task.rejectPlanConfirmButton")}
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  setRejectOpen(false);
                  setReason("");
                }}
                disabled={rejectMutation.isPending}
              >
                {t("task.rejectPlanCancel")}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Stack>
    </Alert>
  );
}
